import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Building2, GraduationCap, Lightbulb, Users } from "lucide-react";

// Shea's actual photos
const SHEA_AWARD_PHOTO = "https://customer-assets.emergentagent.com/job_ai-leadership-5/artifacts/um94twy5_IMG_7419.jpeg";

const achievements = [
  { icon: Building2, label: "Serial Entrepreneur" },
  { icon: GraduationCap, label: "EdTech Leader" },
  { icon: Lightbulb, label: "AI Thought Leader" },
  { icon: Users, label: "Media Personality" },
];

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      id="about"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 relative"
      data-testid="about-section"
    >
      {/* Background elements */}
      <div className="absolute top-1/2 left-0 w-64 h-64 gradient-blob" />
      
      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="relative frame-border">
              <img
                src={SHEA_AWARD_PHOTO}
                alt="Shea Richburg with Berkeley College Award"
                className="relative w-full aspect-[4/5] object-cover object-top rounded-lg"
                data-testid="about-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-accent/10 rounded-lg" />
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7"
          >
            {/* Overline */}
            <span className="text-xs font-['Orbitron'] uppercase tracking-[0.2em] text-primary mb-4 block">
              About Shea
            </span>

            {/* Heading */}
            <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
              Guiding the
              <br />
              <span className="gradient-text-purple">Future</span>
            </h2>

            {/* Bio */}
            <div className="space-y-4 text-base sm:text-lg leading-relaxed text-muted-foreground mb-8">
              <p>
                Shea Richburg is a serial entrepreneur, edtech innovator, and influential voice 
                at the intersection of AI, business transformation, and the future of work. 
                With a career spanning multiple successful ventures, Shea has consistently 
                been at the forefront of technological disruption.
              </p>
              <p>
                As a thought leader and media personality, she provides practical guidance 
                for professionals navigating the complexities of AI-driven change. Her work 
                empowers individuals and organizations to embrace reinvention, build resilience, 
                and position themselves for success.
              </p>
              <p>
                Through her media presence on the <span className="text-primary">African Diaspora News Network</span> and her 
                speaking engagements worldwide, Shea delivers insights that are intelligent, 
                credible, and deeply actionable.
              </p>
            </div>

            {/* Achievement Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {achievements.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="neon-card p-4 text-center"
                >
                  <item.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {item.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
